import type { Client, RequestContext } from "../client";
import type { UseDefaultBranch, BranchSpecifier } from "../repositories/projectRepository";
import type { PaginatedCollection, GitBranchResource, GitTagResource, GitRef, ValidateGitRefV2Response, IIdName, PlatformHubVersionControlSettingsResource } from "../resources/index";

export class PlatformHubRepository {
    protected readonly client: Client;
    protected readonly context: RequestContext;

    protected apiBaseRoute: string = "~/api/platformhub";

    constructor(client: Client, requestContext: RequestContext) {
        this.client = client;
        this.context = requestContext;
    }

    async tryGetBranch(defaultBranch: string, selectedRef: string): Promise<GitBranchResource | null> {
        function ShouldUseDefaultBranch(branch: BranchSpecifier): branch is typeof UseDefaultBranch {
            return typeof branch === "object";
        }

        try {
            const branchName: string = ShouldUseDefaultBranch(selectedRef) ? defaultBranch : selectedRef;
            return await this.client.get(this.context, `${this.apiBaseRoute}/git/branches/{/name}`, { name: branchName });
        } catch (ex) {
            if (ex.StatusCode === 404) {
                return null;
            }
            throw ex;
        }
    }

    getBranches() {
        return this.client.get<PaginatedCollection<GitBranchResource>>(this.context, `${this.apiBaseRoute}/git/branches`);
    }

    searchBranches(partialBranchName: string): Promise<PaginatedCollection<GitBranchResource>> {
        return this.client.get(this.context, `${this.apiBaseRoute}/git/branches{?searchByName}`, { searchByName: partialBranchName });
    }

    searchTags(partialBranchName: string): Promise<PaginatedCollection<GitTagResource>> {
        return this.client.get(this.context, `${this.apiBaseRoute}/git/tags{?searchByName}`, { searchByName: partialBranchName });
    }

    getTags(): Promise<PaginatedCollection<GitTagResource>> {
        return this.client.get(this.context, `${this.apiBaseRoute}/git/tags`);
    }

    validateGitRef(gitRef: GitRef): Promise<ValidateGitRefV2Response> {
        return this.client.post(this.context, `~/bff/platformhub/git/validate`, { GitRef: gitRef });
    }

    createBranch(newBranchName: string, baseGitRef: GitRef): Promise<GitBranchResource> {
        return this.client.post<GitBranchResource>(this.context, `${this.apiBaseRoute}/git/branches`, { NewBranchName: newBranchName, BaseGitRef: baseGitRef });
    }

    getEnvironmentSummaries() {
        return this.client.get<EnvironmentSpaceInfo[]>(this.context, "~/bff/environments");
    }

    async requestTrial() {
        return await this.client.post(this.context, "~/bff/platformhub/request-platformhub-trial");
    }

    getVersionControlSettings() {
        return this.client.get<PlatformHubVersionControlSettingsResource>(this.context, `${this.apiBaseRoute}/versioncontrol`);
    }

    modifyVersionControlSettings(persistenceSettings: PlatformHubVersionControlSettingsResource) {
        return this.client.put<PlatformHubVersionControlSettingsResource>(this.context, `${this.apiBaseRoute}/versioncontrol`, persistenceSettings);
    }

    getDeploymentPermissionsToEnvironmentReport(environmentIds: string[]) {
        return this.client.get<DeploymentPermissionsReportResponse>(this.context, "~/bff/platformhub/compliance-reports/deployment-permissions{?environmentIds*}", { environmentIds });
    }

    downloadDeploymentPermissionsReportCsv(environmentIds: string[]): void {
        const args = { environmentIds };
        const csvUrl = this.client.resolve("~/bff/platformhub/compliance-reports/deployment-permissions/csv{?environmentIds*}", args);
        window.location.assign(csvUrl);
    }
}

export interface EnvironmentSpaceInfo {
    Id: string;
    Name: string;
    SpaceId: string;
    SpaceName: string;
}

export interface PlatformHubAccountSummary extends IIdName {}

export interface DeploymentPermissionsReportResponse {
    Results: DeploymentPermissionsReportEnvironmentResult[];
}

export interface DeploymentPermissionsReportEnvironmentResult {
    EnvironmentName: string;
    EnvironmentId: string;
    SpaceId: string;
    SpaceName: string;
    Projects: DeploymentPermissionsReportProjectResult[];
}

export interface DeploymentPermissionsReportProjectResult {
    ProjectName: string;
    ProjectLogo: string;
    ProjectId: string;
    ProjectSlug: string;
    Users: UserWithPermissionToDeploy[];
}

export interface UserWithPermissionToDeploy {
    UserId: string;
    Username: string;
    UserDisplayName: string;
    UserEmail?: string;
}
